import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsLeftRight, faCheck, faX } from "@fortawesome/pro-regular-svg-icons";
import Spot from "@/shared/components/PolicyCalloutTest/assets/spot.svg";
import { cn } from "@/shared/utils";
import { PublicConfig } from "@/shared/PublicConfig";
import Strings from "@/shared/utils/Strings.constants";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons";

interface Provider {
    id: string;
    name: string;
    logo?: React.ReactNode;
}

interface Feature {
    name: string;
    footnote?: string;
    coverage: Record<string, boolean>;
}

interface Category {
    name: string;
    features: Feature[];
}

const AGG_STRINGS = Strings.AGGREGATOR_COMPARISON;

const providers: Provider[] = [
    { id: "healthy-paws", name: AGG_STRINGS.PROVIDER_HEALTHY_PAWS },
    { id: "lemonade", name: AGG_STRINGS.PROVIDER_LEMONADE },
    { id: "pets-best", name: AGG_STRINGS.PROVIDER_PETS_BEST },
    { id: "progressive", name: AGG_STRINGS.PROVIDER_PROGRESSIVE },
    { id: "metlife", name: AGG_STRINGS.PROVIDER_METLIFE },
    { id: "fetch", name: AGG_STRINGS.PROVIDER_FETCH },
    { id: "figo", name: AGG_STRINGS.PROVIDER_FIGO },
    { id: "embrace", name: AGG_STRINGS.PROVIDER_EMBRACE },
    { id: "aspca", name: AGG_STRINGS.PROVIDER_ASPCA },
    { id: "pumpkin", name: AGG_STRINGS.PROVIDER_PUMPKIN }
];

const comparisonData: Category[] = [
    {
        name: AGG_STRINGS.CATEGORY_MORE_PERKS,
        features: [
            {
                name: AGG_STRINGS.MODAL_FOOTNOTE_PERKS,
                footnote: "2",
                coverage: {
                    spot: true,
                    "healthy-paws": false,
                    lemonade: false,
                    "pets-best": false,
                    progressive: false,
                    metlife: false,
                    fetch: true,
                    figo: false,
                    embrace: true,
                    aspca: false,
                    pumpkin: false
                }
            },
            {
                name: "10% multi-pet discount",
                footnote: "5",
                coverage: {
                    spot: true,
                    "healthy-paws": false,
                    lemonade: true,
                    "pets-best": false,
                    progressive: false,
                    metlife: false,
                    fetch: false,
                    figo: false,
                    embrace: true,
                    aspca: true,
                    pumpkin: true
                }
            },
            {
                name: "30-day money-back guarantee",
                footnote: "6",
                coverage: {
                    spot: true,
                    "healthy-paws": true,
                    lemonade: true,
                    "pets-best": true,
                    progressive: true,
                    metlife: false,
                    fetch: false,
                    figo: true,
                    embrace: true,
                    aspca: true,
                    pumpkin: true
                }
            },
            {
                name: AGG_STRINGS.FEATURE_TELEHEALTH,
                coverage: {
                    spot: true,
                    "healthy-paws": false,
                    lemonade: false,
                    "pets-best": true,
                    progressive: true,
                    metlife: true,
                    fetch: true,
                    figo: true,
                    embrace: true,
                    aspca: true,
                    pumpkin: true
                }
            }
        ]
    },
    {
        name: AGG_STRINGS.CATEGORY_BETTER_COVERAGE,
        features: [
            {
                name: AGG_STRINGS.FEATURE_MICROCHIPPING,
                coverage: {
                    spot: true,
                    "healthy-paws": false,
                    lemonade: false,
                    "pets-best": false,
                    progressive: false,
                    metlife: false,
                    fetch: false,
                    figo: false,
                    embrace: false,
                    aspca: true,
                    pumpkin: true
                }
            },
            {
                name: AGG_STRINGS.FEATURE_PRESCRIPTION_FOOD,
                footnote: "4",
                coverage: {
                    spot: true,
                    "healthy-paws": false,
                    lemonade: false,
                    "pets-best": false,
                    progressive: false,
                    metlife: true,
                    fetch: false,
                    figo: false,
                    embrace: false,
                    aspca: true,
                    pumpkin: true
                }
            },
            {
                name: "Exam fees",
                coverage: {
                    spot: true,
                    "healthy-paws": false,
                    lemonade: false,
                    "pets-best": false,
                    progressive: false,
                    metlife: true,
                    fetch: true,
                    figo: false,
                    embrace: false,
                    aspca: true,
                    pumpkin: true
                }
            },
            {
                name: AGG_STRINGS.FEATURE_ALTERNATIVE_THERAPIES,
                coverage: {
                    spot: true,
                    "healthy-paws": true,
                    lemonade: false,
                    "pets-best": false,
                    progressive: false,
                    metlife: true,
                    fetch: true,
                    figo: true,
                    embrace: true,
                    aspca: true,
                    pumpkin: true
                }
            },
            {
                name: "Behavioral issues",
                coverage: {
                    spot: true,
                    "healthy-paws": false,
                    lemonade: false,
                    "pets-best": true,
                    progressive: true,
                    metlife: false,
                    fetch: true,
                    figo: true,
                    embrace: true,
                    aspca: true,
                    pumpkin: true
                }
            },
            {
                name: AGG_STRINGS.FEATURE_DENTAL_ILLNESSES,
                footnote: "3",
                coverage: {
                    spot: true,
                    "healthy-paws": false,
                    lemonade: false,
                    "pets-best": true,
                    progressive: true,
                    metlife: true,
                    fetch: true,
                    figo: true,
                    embrace: true,
                    aspca: true,
                    pumpkin: true
                }
            }
        ]
    },
    {
        name: AGG_STRINGS.CATEGORY_MORE_FLEXIBILITY,
        features: [
            {
                name: "Accident-only coverage available",
                coverage: {
                    spot: true,
                    "healthy-paws": false,
                    lemonade: false,
                    "pets-best": true,
                    progressive: true,
                    metlife: false,
                    fetch: false,
                    figo: false,
                    embrace: true,
                    aspca: true,
                    pumpkin: false
                }
            },
            {
                name: "No annual checkup required",
                coverage: {
                    spot: true,
                    "healthy-paws": false,
                    lemonade: true,
                    "pets-best": true,
                    progressive: false,
                    metlife: true,
                    fetch: false,
                    figo: false,
                    embrace: false,
                    aspca: true,
                    pumpkin: true
                }
            },
            {
                name: AGG_STRINGS.FEATURE_NO_LIFETIME_CAPS,
                coverage: {
                    spot: true,
                    "healthy-paws": true,
                    lemonade: false,
                    "pets-best": true,
                    progressive: false,
                    metlife: false,
                    fetch: false,
                    figo: true,
                    embrace: true,
                    aspca: true,
                    pumpkin: true
                }
            },
            {
                name: "No upper age limits",
                coverage: {
                    spot: true,
                    "healthy-paws": false,
                    lemonade: false,
                    "pets-best": true,
                    progressive: true,
                    metlife: true,
                    fetch: true,
                    figo: true,
                    embrace: false,
                    aspca: true,
                    pumpkin: true
                }
            }
        ]
    }
];

const ComparisonTableModalContent: React.FC = () => {
    return (
        <div className="flex flex-col gap-6 overflow-x-hidden">
            {/* Gradient Overlay */}
            <div className="absolute right-0 z-10 h-[90%] w-8 bg-gradient-to-r from-[rgba(255,255,255,0)] via-50% to-[#FFF] sm:w-12">&nbsp;</div>
            {/* Heading */}
            <div className="flex flex-col gap-1 text-center">
                <h3 className="text-sm font-bold text-content-brand-b">
                    {AGG_STRINGS.MODAL_HEADING}
                    <sup className="text-content-brand-b opacity-50">1</sup>
                </h3>
                <div className="flex items-center justify-center gap-2">
                    <span className="text-xs font-medium text-content-secondary">{AGG_STRINGS.MODAL_SUBHEADING}</span>
                    <FontAwesomeIcon className="size-4" icon={faArrowsLeftRight} />
                </div>
            </div>
            {/* Table */}
            <div className="-mx-6 overflow-x-auto px-6">
                <div className="min-w-[800px]">
                    {/* Header Row */}
                    <div className="bg-white">
                        <div className="flex items-center justify-stretch">
                            <div className="w-[150px] shrink-0 py-2 ps-2 text-sm font-bold">&nbsp;</div>
                            <div className="flex min-w-[60px] flex-1 justify-center rounded-t-lg bg-content-brand px-1 py-2">
                                <Spot className="h-5 border-0 brightness-0 invert" />
                            </div>
                            {providers.map(provider => (
                                <div key={provider.id} className="flex h-9 w-[60px] items-center justify-center p-1">
                                    <span className="text-center text-[10px] font-bold leading-[11px] text-content-secondary">{provider.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Categories and Features */}
                    {comparisonData.map((category, categoryIndex) => (
                        <div key={categoryIndex}>
                            {/* Category Header */}
                            <div className="rounded-[4px] bg-background-brand-b-secondary px-2 py-[6px]">
                                <h3 className="text-[11px] font-bold uppercase leading-3 text-content-primary">{category.name}</h3>
                            </div>

                            {/* Features */}
                            {category.features.map((feature, featureIndex) => (
                                <div key={featureIndex} className="flex items-stretch">
                                    <div className="w-[150px] py-2 pe-1 ps-2">
                                        <div className="text-xs font-medium text-content-primary">
                                            {feature.name}
                                            {feature.footnote && <sup className="text-content-secondary">{feature.footnote}</sup>}
                                        </div>
                                    </div>
                                    <div
                                        className={cn(
                                            "flex w-[55px] flex-1 items-center justify-center border-x  border-stroke-brand bg-background-primary p-1",
                                            comparisonData.length === categoryIndex + 1 && category.features.length === featureIndex + 1 && "rounded-b-[6px] border-b"
                                        )}
                                    >
                                        <FontAwesomeIcon className="size-4 text-content-brand" icon={faCheckCircle} />
                                    </div>
                                    {providers.map(provider => (
                                        <div key={provider.id} className="flex w-[60px] flex-1 items-center justify-center p-1">
                                            {feature.coverage[provider.id] ? (
                                                <FontAwesomeIcon className="size-4 text-content-success" icon={faCheck} />
                                            ) : (
                                                <FontAwesomeIcon className="size-4 text-content-brand-secondary" icon={faX} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
            {/* Legend */}
            <div className="mx-auto flex w-fit flex-row gap-3 rounded-full bg-background-secondary px-2 py-1">
                <div className="flex items-center gap-1">
                    <FontAwesomeIcon className="size-4 text-content-success" icon={faCheck} />
                    <span className="text-xs font-medium text-content-secondary">{AGG_STRINGS.TABLE_LEGEND_COVERED}</span>
                </div>
                <div className="flex items-center gap-1">
                    <FontAwesomeIcon className="size-4 text-content-brand-secondary" icon={faX} />
                    <span className="text-xs font-medium text-content-primary">Not Covered / Some Inclusions</span>
                </div>
            </div>
            {/* Sample Policy */}
            <div className="pb-1 text-center text-xs font-medium text-content-secondary">
                For a list of exclusions, please refer to a{" "}
                <a className="inline-block whitespace-nowrap underline" href={PublicConfig.PTZ_US.SPOT_MARKETING_URL + PublicConfig.PTZ_US.SAMPLE_POLICY_PATH} target="_blank">
                    sample policy
                </a>
            </div>
        </div>
    );
};

export default ComparisonTableModalContent;
